import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Users,
  UserPlus,
  Search,
  Filter,
  Pencil,
  Trash2,
  AlertTriangle,
  Star,
  Phone,
  CreditCard,
  Briefcase,
  History,
  Plus,
  Eye,
  EyeOff,
  Loader2,
  ShieldAlert,
  Award,
  FileWarning,
  Ban,
  MessageSquare,
  DollarSign,
  Calendar,
  Store,
  Utensils,
} from 'lucide-react';
import { toast } from 'sonner';
import { humanApi, fornecedorRefeicaoApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const HUMAN_TYPES = [
  { value: 'colaborador', label: 'Colaborador', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'terceiro', label: 'Terceiro', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
];

const THIRD_PARTY_SUBTYPES = [
  { value: 'ajudante', label: 'Ajudante' },
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'motorista_caminhao', label: 'Motorista de Caminhão' },
  { value: 'faxineira', label: 'Faxineira' },
  { value: 'tecnico', label: 'Técnico' },
  { value: 'outros', label: 'Outros' },
];

const STATUS_OPTIONS = [
  { value: 'ativo', label: 'Ativo', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'inativo', label: 'Inativo', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400' },
  { value: 'bloqueado', label: 'Bloqueado', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
];

const OCCURRENCE_TYPES = [
  { value: 'advertencia_verbal', label: 'Advertência Verbal', icon: MessageSquare, color: 'text-amber-500' },
  { value: 'advertencia_escrita', label: 'Advertência Escrita', icon: FileWarning, color: 'text-orange-500' },
  { value: 'suspensao', label: 'Suspensão', icon: Ban, color: 'text-red-500' },
  { value: 'incidente_grave', label: 'Incidente Grave', icon: ShieldAlert, color: 'text-red-700' },
  { value: 'elogio', label: 'Elogio/Destaque', icon: Award, color: 'text-green-500' },
];

const FORNECEDOR_TIPOS = [
  { value: 'restaurante', label: 'Restaurante', icon: '🍽️' },
  { value: 'quentinha', label: 'Quentinha/Marmitex', icon: '🥡' },
  { value: 'vale_refeicao', label: 'Vale Refeição', icon: '💳' },
  { value: 'lanchonete', label: 'Lanchonete', icon: '🥪' },
  { value: 'outro', label: 'Outro', icon: '📦' },
];

const FREQUENCIA_PAGAMENTO = [
  { value: 'diario', label: 'Diário' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'quinzenal', label: 'Quinzenal' },
  { value: 'mensal', label: 'Mensal' },
];

const HumanasPage = () => {
  const { user, isAdmin } = useAuth();
  const canSeeStrategic = ['super_admin', 'admin', 'diretoria'].includes(user?.role);
  
  // Tab principal
  const [mainTab, setMainTab] = useState('pessoas');
  
  const [humans, setHumans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('ativo');
  
  // ========= FORNECEDORES DE REFEIÇÃO =========
  const [fornecedores, setFornecedores] = useState([]);
  const [loadingFornecedores, setLoadingFornecedores] = useState(false);
  const [showFornecedorDialog, setShowFornecedorDialog] = useState(false);
  const [editingFornecedor, setEditingFornecedor] = useState(null);
  const [fornecedorForm, setFornecedorForm] = useState({
    nome: '',
    tipo: 'restaurante',
    valor_unitario: '',
    frequencia_pagamento: 'mensal',
    contato: '',
    observacoes: ''
  });
  const [savingFornecedor, setSavingFornecedor] = useState(false);
  
  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showOccurrenceDialog, setShowOccurrenceDialog] = useState(false);
  const [selectedHuman, setSelectedHuman] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'colaborador',
    subtype: '',
    contract_type: 'clt',  // clt (horista) ou diaria (terceiro)
    pix: '',
    phone: '',
    skills: '',
    notes: '',
    default_daily_rate: '',
    hourly_rate: '',
    is_strategic: false,
    status: 'ativo',
    // Campos CLT
    salario_base: '',
    beneficio_mensal: '',
    beneficio_lider: '',
    beneficio_extra: '',
    conducao_mensal: '',
    // Controle de VT
    tipo_conducao: 'valor_fixo', // 'valor_fixo' ou 'vt'
    valor_passagem_vt: '',
    // Novos campos CLT detalhados
    data_admissao: '',
    ctps: '',
    pis_pasep: '',
    jornada: '44h semanais'
  });
  
  // Occurrence form
  const [occurrenceData, setOccurrenceData] = useState({
    type: 'advertencia_verbal',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd')
  });
  
  // Ficha CLT - Histórico de Salário
  const [salarioHistorico, setSalarioHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [showNovaVigenciaDialog, setShowNovaVigenciaDialog] = useState(false);
  const [novaVigencia, setNovaVigencia] = useState({
    competencia_inicio: format(new Date(), 'yyyy-MM'),
    cargo: '',
    salario_base: '',
    motivo: 'contratacao',
    justificativa: '',
    aceita_reducao: false
  });
  const [savingVigencia, setSavingVigencia] = useState(false);
  
  useEffect(() => {
    loadHumans();
  }, [typeFilter, statusFilter]);
  
  const loadHumans = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await humanApi.list(params);
      setHumans(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pessoas');
    } finally {
      setLoading(false);
    }
  };
  
  // ========= FUNÇÕES FORNECEDORES =========
  const loadFornecedores = async () => {
    setLoadingFornecedores(true);
    try {
      const res = await fornecedorRefeicaoApi.list(null); // null = todos, true = ativos
      setFornecedores(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoadingFornecedores(false);
    }
  };
  
  const resetFornecedorForm = () => {
    setFornecedorForm({
      nome: '',
      tipo: 'restaurante',
      valor_unitario: '',
      frequencia_pagamento: 'mensal',
      contato: '',
      observacoes: ''
    });
    setEditingFornecedor(null);
  };
  
  const handleSaveFornecedor = async () => {
    if (!fornecedorForm.nome.trim()) {
      toast.error('Digite o nome do fornecedor');
      return;
    }
    
    setSavingFornecedor(true);
    try {
      const data = {
        ...fornecedorForm,
        valor_unitario: fornecedorForm.valor_unitario ? parseFloat(fornecedorForm.valor_unitario) : 0
      };
      
      if (editingFornecedor) {
        await fornecedorRefeicaoApi.update(editingFornecedor.id, data);
        toast.success('Fornecedor atualizado!');
      } else {
        await fornecedorRefeicaoApi.create(data);
        toast.success('Fornecedor cadastrado!');
      }
      
      setShowFornecedorDialog(false);
      resetFornecedorForm();
      loadFornecedores();
    } catch (error) {
      toast.error('Erro ao salvar fornecedor');
    } finally {
      setSavingFornecedor(false);
    }
  };
  
  const handleDeleteFornecedor = async (fornecedor) => {
    if (!confirm(`Desativar fornecedor "${fornecedor.nome}"?`)) return;
    
    try {
      await fornecedorRefeicaoApi.delete(fornecedor.id);
      toast.success('Fornecedor desativado');
      loadFornecedores();
    } catch (error) {
      toast.error('Erro ao desativar fornecedor');
    }
  };
  
  const openEditFornecedor = (fornecedor) => {
    setEditingFornecedor(fornecedor);
    setFornecedorForm({
      nome: fornecedor.nome || '',
      tipo: fornecedor.tipo || 'restaurante',
      valor_unitario: fornecedor.valor_unitario || '',
      frequencia_pagamento: fornecedor.frequencia_pagamento || 'mensal',
      contato: fornecedor.contato || '',
      observacoes: fornecedor.observacoes || ''
    });
    setShowFornecedorDialog(true);
  };
  
  // Carregar fornecedores quando mudar para aba de fornecedores
  useEffect(() => {
    if (mainTab === 'fornecedores') {
      loadFornecedores();
    }
  }, [mainTab]);
  
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'colaborador',
      subtype: '',
      contract_type: 'clt',
      pix: '',
      phone: '',
      skills: '',
      notes: '',
      default_daily_rate: '',
      hourly_rate: '',
      is_strategic: false,
      status: 'ativo',
      salario_base: '',
      beneficio_mensal: '',
      beneficio_lider: '',
      beneficio_extra: '',
      conducao_mensal: '',
      tipo_conducao: 'valor_fixo',
      valor_passagem_vt: '',
      data_admissao: '',
      ctps: '',
      pis_pasep: '',
      jornada: '44h semanais'
    });
  };
  
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Digite o nome');
      return;
    }
    
    setSaving(true);
    try {
      // Converter campos numéricos - strings vazias viram null
      const data = {
        ...formData,
        default_daily_rate: formData.default_daily_rate ? parseFloat(formData.default_daily_rate) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        salario_base: formData.salario_base ? parseFloat(formData.salario_base) : null,
        beneficio_mensal: formData.beneficio_mensal ? parseFloat(formData.beneficio_mensal) : null,
        beneficio_lider: formData.beneficio_lider ? parseFloat(formData.beneficio_lider) : null,
        beneficio_extra: formData.beneficio_extra ? parseFloat(formData.beneficio_extra) : null,
        conducao_mensal: formData.conducao_mensal ? parseFloat(formData.conducao_mensal) : null,
        tipo_conducao: formData.tipo_conducao || 'valor_fixo',
        valor_passagem_vt: formData.valor_passagem_vt ? parseFloat(formData.valor_passagem_vt) : null,
      };
      await humanApi.create(data);
      toast.success('Pessoa cadastrada!');
      setShowNewDialog(false);
      resetForm();
      loadHumans();
    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Digite o nome');
      return;
    }
    
    setSaving(true);
    try {
      // Converter campos numéricos - strings vazias viram null
      const data = {
        ...formData,
        default_daily_rate: formData.default_daily_rate ? parseFloat(formData.default_daily_rate) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        salario_base: formData.salario_base ? parseFloat(formData.salario_base) : null,
        beneficio_mensal: formData.beneficio_mensal ? parseFloat(formData.beneficio_mensal) : null,
        beneficio_lider: formData.beneficio_lider ? parseFloat(formData.beneficio_lider) : null,
        beneficio_extra: formData.beneficio_extra ? parseFloat(formData.beneficio_extra) : null,
        conducao_mensal: formData.conducao_mensal ? parseFloat(formData.conducao_mensal) : null,
        tipo_conducao: formData.tipo_conducao || 'valor_fixo',
        valor_passagem_vt: formData.valor_passagem_vt ? parseFloat(formData.valor_passagem_vt) : null,
      };
      await humanApi.update(selectedHuman.id, data);
      toast.success('Dados atualizados!');
      setShowEditDialog(false);
      loadHumans();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (human) => {
    if (!window.confirm(`Desativar ${human.name}?`)) return;
    
    try {
      await humanApi.delete(human.id);
      toast.success('Pessoa desativada');
      loadHumans();
    } catch (error) {
      toast.error('Erro ao desativar');
    }
  };
  
  const openEditDialog = (human) => {
    setSelectedHuman(human);
    setFormData({
      name: human.name || '',
      type: human.type || 'colaborador',
      subtype: human.subtype || '',
      contract_type: human.contract_type || (human.type === 'terceiro' ? 'diaria' : 'clt'),
      pix: human.pix || '',
      phone: human.phone || '',
      skills: human.skills || '',
      notes: human.notes || '',
      default_daily_rate: human.default_daily_rate || '',
      hourly_rate: human.hourly_rate || '',
      is_strategic: human.is_strategic || false,
      status: human.status || 'ativo',
      salario_base: human.salario_base || '',
      beneficio_mensal: human.beneficio_mensal || '',
      beneficio_lider: human.beneficio_lider || '',
      beneficio_extra: human.beneficio_extra || '',
      conducao_mensal: human.conducao_mensal || '',
      tipo_conducao: human.tipo_conducao || 'valor_fixo',
      valor_passagem_vt: human.valor_passagem_vt || '',
      // Campos CLT detalhados
      data_admissao: human.data_admissao || '',
      ctps: human.ctps || '',
      pis_pasep: human.pis_pasep || '',
      jornada: human.jornada || '44h semanais'
    });
    setShowEditDialog(true);
  };
  
  const openDetailsDialog = async (human) => {
    try {
      const response = await humanApi.get(human.id);
      setSelectedHuman(response.data);
      setShowDetailsDialog(true);
      
      // Se for CLT, carregar histórico de salário
      if (response.data.contract_type === 'clt') {
        loadSalarioHistorico(response.data.id);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };
  
  const handleAddOccurrence = async () => {
    if (!occurrenceData.description.trim()) {
      toast.error('Digite a descrição');
      return;
    }
    
    setSaving(true);
    try {
      await humanApi.addOccurrence(selectedHuman.id, occurrenceData);
      toast.success('Ocorrência registrada!');
      setShowOccurrenceDialog(false);
      setOccurrenceData({ type: 'advertencia_verbal', description: '', date: format(new Date(), 'yyyy-MM-dd') });
      // Refresh details
      const response = await humanApi.get(selectedHuman.id);
      setSelectedHuman(response.data);
    } catch (error) {
      toast.error('Erro ao registrar ocorrência');
    } finally {
      setSaving(false);
    }
  };
  
  // Carregar histórico de salário do CLT
  const loadSalarioHistorico = async (humanId) => {
    if (!humanId) return;
    setLoadingHistorico(true);
    try {
      const response = await humanApi.getSalarioHistorico(humanId);
      setSalarioHistorico(response.data?.historico || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setSalarioHistorico([]);
    } finally {
      setLoadingHistorico(false);
    }
  };
  
  // Adicionar nova vigência de salário
  const handleAddVigencia = async () => {
    if (!novaVigencia.cargo.trim() || !novaVigencia.salario_base) {
      toast.error('Preencha cargo e salário');
      return;
    }
    
    if (novaVigencia.motivo === 'correcao_erro' && !novaVigencia.justificativa?.trim()) {
      toast.error('Correção de erro requer justificativa');
      return;
    }
    
    setSavingVigencia(true);
    try {
      await humanApi.addSalarioVigencia(selectedHuman.id, {
        competencia_inicio: novaVigencia.competencia_inicio,
        cargo: novaVigencia.cargo.trim(),
        salario_base: parseFloat(novaVigencia.salario_base),
        motivo: novaVigencia.motivo,
        justificativa: novaVigencia.justificativa?.trim() || null,
        aceita_reducao: novaVigencia.aceita_reducao
      });
      toast.success('Vigência registrada com sucesso!');
      setShowNovaVigenciaDialog(false);
      setNovaVigencia({
        competencia_inicio: format(new Date(), 'yyyy-MM'),
        cargo: '',
        salario_base: '',
        motivo: 'contratacao',
        justificativa: '',
        aceita_reducao: false
      });
      // Recarregar histórico
      loadSalarioHistorico(selectedHuman.id);
      // Atualizar dados do human
      const response = await humanApi.get(selectedHuman.id);
      setSelectedHuman(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao registrar vigência';
      toast.error(msg);
    } finally {
      setSavingVigencia(false);
    }
  };
  
  const filteredHumans = humans.filter(h => 
    h.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.pix?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.skills?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const getTypeInfo = (type) => HUMAN_TYPES.find(t => t.value === type) || HUMAN_TYPES[0];
  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  const getSubtypeLabel = (subtype) => THIRD_PARTY_SUBTYPES.find(s => s.value === subtype)?.label || subtype;
  const getOccurrenceType = (type) => OCCURRENCE_TYPES.find(o => o.value === type) || OCCURRENCE_TYPES[0];
  const getTipoFornecedor = (tipo) => FORNECEDOR_TIPOS.find(t => t.value === tipo) || FORNECEDOR_TIPOS[0];
  const getFrequenciaPagamento = (freq) => FREQUENCIA_PAGAMENTO.find(f => f.value === freq)?.label || freq;
  
  return (
    <div data-testid="humanas-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            Humanas
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Cadastro de colaboradores, terceiros e fornecedores
          </p>
        </div>
        
        {/* Botões variam de acordo com a aba */}
        {mainTab === 'pessoas' && (
          <Button onClick={() => { resetForm(); setShowNewDialog(true); }} data-testid="new-human-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Nova Pessoa
          </Button>
        )}
        {mainTab === 'fornecedores' && (
          <Button onClick={() => { resetFornecedorForm(); setShowFornecedorDialog(true); }}>
            <Store className="w-4 h-4 mr-2" />
            Novo Fornecedor
          </Button>
        )}
      </div>
      
      {/* Tabs Principais */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="pessoas" className="data-[state=active]:bg-blue-600 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Pessoas
          </TabsTrigger>
          <TabsTrigger value="fornecedores" className="data-[state=active]:bg-emerald-600 flex items-center gap-2">
            <Utensils className="w-4 h-4" />
            Fornecedores de Refeição
          </TabsTrigger>
        </TabsList>
        
        {/* TAB: PESSOAS */}
        <TabsContent value="pessoas" className="mt-4 space-y-6">
      <Card className="dark:bg-slate-800/50 dark:border-slate-700">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome, PIX ou aptidões..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-40 dark:bg-slate-900 dark:border-slate-600">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                <SelectItem value="all" className="dark:text-slate-200">Todos</SelectItem>
                <SelectItem value="colaborador" className="dark:text-slate-200">Colaboradores</SelectItem>
                <SelectItem value="terceiro" className="dark:text-slate-200">Terceiros</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40 dark:bg-slate-900 dark:border-slate-600">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                <SelectItem value="all" className="dark:text-slate-200">Todos</SelectItem>
                <SelectItem value="ativo" className="dark:text-slate-200">Ativos</SelectItem>
                <SelectItem value="inativo" className="dark:text-slate-200">Inativos</SelectItem>
                <SelectItem value="bloqueado" className="dark:text-slate-200">Bloqueados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card className="dark:bg-slate-800/50 dark:border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredHumans.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-slate-500 dark:text-slate-400">Nenhuma pessoa encontrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="dark:border-slate-700">
                  <TableHead className="dark:text-slate-300">Nome</TableHead>
                  <TableHead className="dark:text-slate-300">Tipo</TableHead>
                  <TableHead className="dark:text-slate-300">PIX</TableHead>
                  <TableHead className="dark:text-slate-300">Aptidões</TableHead>
                  <TableHead className="dark:text-slate-300 text-center">Ocorrências</TableHead>
                  <TableHead className="dark:text-slate-300 text-center">Apontamentos</TableHead>
                  <TableHead className="dark:text-slate-300">Status</TableHead>
                  <TableHead className="dark:text-slate-300 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHumans.map((human) => {
                  const typeInfo = getTypeInfo(human.type);
                  const statusInfo = getStatusInfo(human.status);
                  
                  return (
                    <TableRow key={human.id} className="dark:border-slate-700 group">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium dark:text-slate-200">{human.name}</span>
                          {human.is_strategic && (
                            <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700">
                              <EyeOff className="w-3 h-3 mr-1" />
                              Diretoria
                            </Badge>
                          )}
                        </div>
                        {human.subtype && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">{getSubtypeLabel(human.subtype)}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400">
                        {human.pix || '-'}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600 dark:text-slate-400 truncate max-w-xs">
                          {human.skills || '-'}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        {human.occurrence_count > 0 ? (
                          <Badge variant="outline" className={human.occurrence_count > 2 ? 'border-red-300 text-red-600 dark:border-red-700 dark:text-red-400' : 'dark:border-slate-600 dark:text-slate-400'}>
                            {human.occurrence_count}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {human.total_appointments > 0 ? (
                          <div className="text-center">
                            <p className="text-sm font-medium dark:text-slate-200">{human.total_appointments}</p>
                            {human.total_value > 0 && (
                              <p className="text-xs text-green-600 dark:text-green-400">
                                R$ {human.total_value.toFixed(2)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDetailsDialog(human)}
                            className="h-8 w-8 dark:hover:bg-slate-700"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(human)}
                            className="h-8 w-8 dark:hover:bg-slate-700"
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          {isAdmin() && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(human)}
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                              title="Desativar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </TabsContent>
      
      {/* TAB: FORNECEDORES DE REFEIÇÃO */}
      <TabsContent value="fornecedores" className="mt-4">
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="dark:text-slate-100 flex items-center gap-2">
              <Utensils className="w-5 h-5 text-emerald-500" />
              Fornecedores de Refeição
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Cadastre restaurantes, marmitex e outros fornecedores de alimentação
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingFornecedores ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : fornecedores.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <Store className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Nenhum fornecedor cadastrado</p>
                <Button 
                  onClick={() => { resetFornecedorForm(); setShowFornecedorDialog(true); }} 
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Cadastrar Primeiro Fornecedor
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="dark:border-slate-700">
                    <TableHead className="dark:text-slate-300">Fornecedor</TableHead>
                    <TableHead className="dark:text-slate-300">Tipo</TableHead>
                    <TableHead className="dark:text-slate-300 text-right">Valor Unit.</TableHead>
                    <TableHead className="dark:text-slate-300">Frequência Pgto</TableHead>
                    <TableHead className="dark:text-slate-300">Contato</TableHead>
                    <TableHead className="dark:text-slate-300">Status</TableHead>
                    <TableHead className="dark:text-slate-300 w-24">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fornecedores.map((forn) => {
                    const tipoInfo = getTipoFornecedor(forn.tipo);
                    return (
                      <TableRow key={forn.id} className="dark:border-slate-700">
                        <TableCell className="font-medium dark:text-slate-200">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{tipoInfo.icon}</span>
                            {forn.nome}
                          </div>
                        </TableCell>
                        <TableCell className="dark:text-slate-300">
                          {tipoInfo.label}
                        </TableCell>
                        <TableCell className="text-right dark:text-emerald-400 font-mono font-medium">
                          {forn.valor_unitario ? `R$ ${forn.valor_unitario.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell className="dark:text-slate-300">
                          <Badge variant="outline" className="dark:border-slate-600">
                            {getFrequenciaPagamento(forn.frequencia_pagamento)}
                          </Badge>
                        </TableCell>
                        <TableCell className="dark:text-slate-400 text-sm">
                          {forn.contato || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={forn.ativo !== false ? 'bg-emerald-500' : 'bg-slate-500'}>
                            {forn.ativo !== false ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openEditFornecedor(forn)}
                              className="h-8 w-8 p-0 dark:text-slate-400 dark:hover:text-white"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {forn.ativo !== false && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDeleteFornecedor(forn)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>
      
      {/* Dialog Fornecedor de Refeição */}
      <Dialog open={showFornecedorDialog} onOpenChange={setShowFornecedorDialog}>
        <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              <Utensils className="w-5 h-5 text-emerald-500" />
              {editingFornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {editingFornecedor ? 'Atualize os dados do fornecedor' : 'Cadastre um fornecedor de refeição'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-300">Nome do Fornecedor *</Label>
              <Input
                value={fornecedorForm.nome}
                onChange={(e) => setFornecedorForm({ ...fornecedorForm, nome: e.target.value })}
                placeholder="Ex: Restaurante do João"
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-slate-300">Tipo</Label>
                <Select
                  value={fornecedorForm.tipo}
                  onValueChange={(v) => setFornecedorForm({ ...fornecedorForm, tipo: v })}
                >
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {FORNECEDOR_TIPOS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.icon} {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="dark:text-slate-300">Valor Unitário (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fornecedorForm.valor_unitario}
                  onChange={(e) => setFornecedorForm({ ...fornecedorForm, valor_unitario: e.target.value })}
                  placeholder="0.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Frequência de Pagamento</Label>
              <Select
                value={fornecedorForm.frequencia_pagamento}
                onValueChange={(v) => setFornecedorForm({ ...fornecedorForm, frequencia_pagamento: v })}
              >
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  {FREQUENCIA_PAGAMENTO.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Contato (Telefone/WhatsApp)</Label>
              <Input
                value={fornecedorForm.contato}
                onChange={(e) => setFornecedorForm({ ...fornecedorForm, contato: e.target.value })}
                placeholder="(99) 99999-9999"
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Observações</Label>
              <Textarea
                value={fornecedorForm.observacoes}
                onChange={(e) => setFornecedorForm({ ...fornecedorForm, observacoes: e.target.value })}
                placeholder="Informações adicionais..."
                className="dark:bg-slate-900 dark:border-slate-600"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFornecedorDialog(false)} className="dark:border-slate-600">
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveFornecedor} 
              disabled={savingFornecedor}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingFornecedor && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingFornecedor ? 'Salvar Alterações' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* New/Edit Dialog */}
      <Dialog open={showNewDialog || showEditDialog} onOpenChange={(open) => { if (!open) { setShowNewDialog(false); setShowEditDialog(false); } }}>
        <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">
              {showEditDialog ? 'Editar Pessoa' : 'Nova Pessoa'}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {showEditDialog ? 'Atualize os dados cadastrais' : 'Cadastre um colaborador ou terceiro'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="dark:text-slate-300">Nome Completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              <div>
                <Label className="dark:text-slate-300">Tipo *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v, subtype: v === 'colaborador' ? '' : formData.subtype, contract_type: v === 'terceiro' ? 'diaria' : 'clt' })}>
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {HUMAN_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value} className="dark:text-slate-200">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="dark:text-slate-300">Forma de Pagamento *</Label>
                <Select value={formData.contract_type} onValueChange={(v) => setFormData({ ...formData, contract_type: v })}>
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    <SelectItem value="clt" className="dark:text-slate-200">CLT (Horista) - Paga por hora</SelectItem>
                    <SelectItem value="diaria" className="dark:text-slate-200">Diária (Freelancer) - Paga por dia</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  CLT = apura horas extras | Diária = conta dias trabalhados
                </p>
              </div>
              
              {formData.type === 'terceiro' && (
                <div>
                  <Label className="dark:text-slate-300">Subtipo</Label>
                  <Select value={formData.subtype} onValueChange={(v) => setFormData({ ...formData, subtype: v })}>
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      {THIRD_PARTY_SUBTYPES.map(s => (
                        <SelectItem key={s.value} value={s.value} className="dark:text-slate-200">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="dark:text-slate-300">Chave PIX</Label>
                <Input
                  value={formData.pix}
                  onChange={(e) => setFormData({ ...formData, pix: e.target.value })}
                  placeholder="CPF, Email, Telefone..."
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              <div>
                <Label className="dark:text-slate-300">Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              {formData.type === 'terceiro' && (
                <div>
                  <Label className="dark:text-slate-300">Valor Diária Padrão</Label>
                  <Input
                    type="number"
                    value={formData.default_daily_rate}
                    onChange={(e) => setFormData({ ...formData, default_daily_rate: e.target.value })}
                    placeholder="R$ 0,00"
                    className="dark:bg-slate-900 dark:border-slate-600"
                  />
                </div>
              )}
              
              {/* Campos CLT - Salário */}
              {formData.contract_type === 'clt' && (
                <>
                  <div className="col-span-2 border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                      💰 Salário CLT
                    </h4>
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-300">Salário Base Mensal *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.salario_base}
                      onChange={(e) => setFormData({ ...formData, salario_base: e.target.value })}
                      placeholder="R$ 0,00"
                      className="dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-300">Tipo Condução</Label>
                    <Select
                      value={formData.tipo_conducao || 'valor_fixo'}
                      onValueChange={(value) => setFormData({ ...formData, tipo_conducao: value })}
                    >
                      <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="valor_fixo">Valor Fixo</SelectItem>
                        <SelectItem value="vt">VT (Controle de Saldo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.tipo_conducao === 'valor_fixo' ? (
                    <div>
                      <Label className="dark:text-slate-300">Condução Mensal (Fixo)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.conducao_mensal}
                        onChange={(e) => setFormData({ ...formData, conducao_mensal: e.target.value })}
                        placeholder="R$ 0,00"
                        className="dark:bg-slate-900 dark:border-slate-600"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="dark:text-slate-300">Valor Passagem (ida+volta)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.valor_passagem_vt}
                        onChange={(e) => setFormData({ ...formData, valor_passagem_vt: e.target.value })}
                        placeholder="R$ 8,80 (2x ida)"
                        className="dark:bg-slate-900 dark:border-slate-600"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">Crédito = Dias úteis × este valor</p>
                    </div>
                  )}
                  
                  <p className="col-span-2 text-[10px] text-slate-500 italic">
                    💡 Benefícios são controlados em Operacional → Controle de Benefícios
                  </p>
                  
                  {/* Dados Trabalhistas CLT */}
                  <div className="col-span-2 border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                      📋 Dados Trabalhistas
                    </h4>
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-300">Data de Admissão</Label>
                    <Input
                      type="date"
                      value={formData.data_admissao}
                      onChange={(e) => setFormData({ ...formData, data_admissao: e.target.value })}
                      className="dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-300">CTPS (Nº/Série)</Label>
                    <Input
                      value={formData.ctps}
                      onChange={(e) => setFormData({ ...formData, ctps: e.target.value })}
                      placeholder="00000/0000"
                      className="dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-300">PIS/PASEP</Label>
                    <Input
                      value={formData.pis_pasep}
                      onChange={(e) => setFormData({ ...formData, pis_pasep: e.target.value })}
                      placeholder="000.00000.00-0"
                      className="dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label className="dark:text-slate-300">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value} className="dark:text-slate-200">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2">
                <Label className="dark:text-slate-300">Aptidões / Descrição</Label>
                <Textarea
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                  placeholder="Descreva as habilidades e experiências..."
                  rows={3}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              <div className="col-span-2">
                <Label className="dark:text-slate-300">Observações</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notas adicionais..."
                  rows={2}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              {canSeeStrategic && formData.type === 'terceiro' && (
                <div className="col-span-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={formData.is_strategic}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_strategic: checked })}
                    />
                    <div>
                      <p className="font-medium text-blue-800 dark:text-blue-300 flex items-center gap-2">
                        <EyeOff className="w-4 h-4" />
                        Terceiro da Diretoria
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Oculto para usuários comuns. Apenas Diretoria e Admin podem ver.
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewDialog(false); setShowEditDialog(false); }} className="dark:border-slate-600">
              Cancelar
            </Button>
            <Button onClick={showEditDialog ? handleUpdate : handleCreate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {showEditDialog ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between dark:text-slate-100">
              <span>{selectedHuman?.name}</span>
              <div className="flex items-center gap-2">
                {selectedHuman?.is_strategic && (
                  <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700">
                    <EyeOff className="w-3 h-3 mr-1" />
                    Diretoria
                  </Badge>
                )}
                <Badge className={getStatusInfo(selectedHuman?.status).color}>
                  {getStatusInfo(selectedHuman?.status).label}
                </Badge>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedHuman && (
            <Tabs defaultValue={selectedHuman.contract_type === 'clt' ? 'ficha_clt' : 'info'} className="w-full">
              <TabsList className={`grid w-full ${selectedHuman.contract_type === 'clt' ? 'grid-cols-4' : 'grid-cols-3'}`}>
                {selectedHuman.contract_type === 'clt' && (
                  <TabsTrigger value="ficha_clt" className="text-blue-600">
                    <DollarSign className="w-3 h-3 mr-1" />
                    Ficha CLT
                  </TabsTrigger>
                )}
                <TabsTrigger value="info">Informações</TabsTrigger>
                <TabsTrigger value="occurrences">
                  Ocorrências ({selectedHuman.occurrences?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="appointments">
                  Apontamentos ({selectedHuman.appointments?.length || 0})
                </TabsTrigger>
              </TabsList>
              
              {/* Aba Ficha CLT - Histórico de Salários */}
              {selectedHuman.contract_type === 'clt' && (
                <TabsContent value="ficha_clt" className="space-y-4 pt-4">
                  {/* Dados Atuais */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Dados Atuais
                      </h4>
                      <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        Vigente
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Cargo</Label>
                        <p className="font-medium dark:text-slate-200">{selectedHuman.cargo || 'Colaborador CLT'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Salário Base</Label>
                        <p className="font-bold text-green-600 dark:text-green-400 text-lg">
                          R$ {(selectedHuman.salario_base || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                    {/* Dados CLT Detalhados */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Data Admissão</Label>
                        <p className="text-sm font-medium dark:text-slate-200">
                          {selectedHuman.data_admissao ? format(new Date(selectedHuman.data_admissao + 'T12:00:00'), 'dd/MM/yyyy') : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">CTPS</Label>
                        <p className="text-sm font-medium dark:text-slate-200">{selectedHuman.ctps || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">PIS/PASEP</Label>
                        <p className="text-sm font-medium dark:text-slate-200">{selectedHuman.pis_pasep || '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Botão para adicionar nova vigência */}
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Histórico de Alterações
                    </h4>
                    <Button size="sm" onClick={() => {
                      setNovaVigencia({
                        competencia_inicio: format(new Date(), 'yyyy-MM'),
                        cargo: selectedHuman.cargo || '',
                        salario_base: '',
                        motivo: salarioHistorico.length === 0 ? 'contratacao' : 'promocao',
                        justificativa: '',
                        aceita_reducao: false
                      });
                      setShowNovaVigenciaDialog(true);
                    }}>
                      <Plus className="w-4 h-4 mr-1" />
                      Nova Alteração
                    </Button>
                  </div>
                  
                  {/* Lista do Histórico */}
                  <ScrollArea className="h-48">
                    {loadingHistorico ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      </div>
                    ) : salarioHistorico.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                        <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma alteração registrada</p>
                        <p className="text-xs mt-1">Clique em Nova Alteracao para registrar a primeira vigencia</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {salarioHistorico.map((vigencia, idx) => (
                          <div 
                            key={vigencia.id || idx} 
                            className={`p-3 border rounded-lg ${idx === 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'dark:border-slate-700'}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={
                                  vigencia.motivo === 'promocao' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  vigencia.motivo === 'contratacao' ? 'bg-green-50 text-green-700 border-green-200' :
                                  vigencia.motivo === 'aditivo' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                  vigencia.motivo === 'reenquadramento' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                  'bg-slate-50 text-slate-700 border-slate-200'
                                }>
                                  {vigencia.motivo === 'promocao' ? '🎉 Promoção' :
                                   vigencia.motivo === 'contratacao' ? '✅ Contratação' :
                                   vigencia.motivo === 'aditivo' ? '📝 Aditivo' :
                                   vigencia.motivo === 'reenquadramento' ? '🔄 Reenquadramento' :
                                   vigencia.motivo === 'correcao_erro' ? '⚠️ Correção' :
                                   vigencia.motivo}
                                </Badge>
                                {idx === 0 && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">ATUAL</Badge>
                                )}
                              </div>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                A partir de {vigencia.competencia_inicio}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-slate-500 dark:text-slate-400">Cargo: </span>
                                <span className="font-medium dark:text-slate-200">{vigencia.cargo}</span>
                              </div>
                              <div>
                                <span className="text-slate-500 dark:text-slate-400">Salário: </span>
                                <span className="font-bold text-green-600 dark:text-green-400">
                                  R$ {(vigencia.salario_base || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                                {vigencia.salario_anterior && vigencia.salario_anterior !== vigencia.salario_base && (
                                  <span className="text-xs text-slate-400 ml-1">
                                    (era R$ {vigencia.salario_anterior.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                                  </span>
                                )}
                              </div>
                            </div>
                            {vigencia.justificativa && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 italic">
                                &ldquo;{vigencia.justificativa}&rdquo;
                              </p>
                            )}
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                              Por: {vigencia.created_by_name} em {vigencia.created_at?.substring(0, 10)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              )}
              
              <TabsContent value="info" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 dark:text-slate-400 text-xs">Tipo</Label>
                    <p className="dark:text-slate-200">{getTypeInfo(selectedHuman.type).label}</p>
                  </div>
                  {selectedHuman.subtype && (
                    <div>
                      <Label className="text-slate-500 dark:text-slate-400 text-xs">Subtipo</Label>
                      <p className="dark:text-slate-200">{getSubtypeLabel(selectedHuman.subtype)}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-slate-500 dark:text-slate-400 text-xs">PIX</Label>
                    <p className="dark:text-slate-200">{selectedHuman.pix || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 dark:text-slate-400 text-xs">Telefone</Label>
                    <p className="dark:text-slate-200">{selectedHuman.phone || '-'}</p>
                  </div>
                  {selectedHuman.default_daily_rate && (
                    <div>
                      <Label className="text-slate-500 dark:text-slate-400 text-xs">Diária Padrão</Label>
                      <p className="text-green-600 dark:text-green-400 font-medium">R$ {selectedHuman.default_daily_rate.toFixed(2)}</p>
                    </div>
                  )}
                </div>
                
                {selectedHuman.skills && (
                  <div>
                    <Label className="text-slate-500 dark:text-slate-400 text-xs">Aptidões</Label>
                    <p className="text-sm dark:text-slate-200 whitespace-pre-wrap">{selectedHuman.skills}</p>
                  </div>
                )}
                
                {selectedHuman.notes && (
                  <div>
                    <Label className="text-slate-500 dark:text-slate-400 text-xs">Observações</Label>
                    <p className="text-sm dark:text-slate-200 whitespace-pre-wrap">{selectedHuman.notes}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="occurrences" className="pt-4">
                <div className="flex justify-end mb-4">
                  <Button size="sm" onClick={() => setShowOccurrenceDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Nova Ocorrência
                  </Button>
                </div>
                
                <ScrollArea className="h-64">
                  {selectedHuman.occurrences?.length === 0 ? (
                    <p className="text-center text-slate-500 dark:text-slate-400 py-8">Nenhuma ocorrência registrada</p>
                  ) : (
                    <div className="space-y-3">
                      {selectedHuman.occurrences?.map((occ) => {
                        const occType = getOccurrenceType(occ.type);
                        const OccIcon = occType.icon;
                        
                        return (
                          <div key={occ.id} className="p-3 border rounded-lg dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-2">
                              <OccIcon className={`w-4 h-4 ${occType.color}`} />
                              <span className="font-medium dark:text-slate-200">{occType.label}</span>
                              <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                                {occ.date && format(new Date(occ.date), "dd/MM/yyyy", { locale: ptBR })}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400">{occ.description}</p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                              Por: {occ.created_by_name}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="appointments" className="pt-4">
                <ScrollArea className="h-64">
                  {selectedHuman.appointments?.length === 0 ? (
                    <p className="text-center text-slate-500 dark:text-slate-400 py-8">Nenhum apontamento registrado</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedHuman.appointments?.map((apt, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border rounded-lg dark:border-slate-700">
                          <div>
                            <p className="font-medium dark:text-slate-200">
                              {apt.date && format(new Date(apt.date), "dd/MM/yyyy", { locale: ptBR })}
                            </p>
                            {apt.service_description && (
                              <p className="text-xs text-slate-500 dark:text-slate-400">{apt.service_description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {apt.value_type === 'acertado_diretoria' ? (
                              <Badge variant="outline" className="text-xs">Acertado c/ Diretoria</Badge>
                            ) : apt.value ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">R$ {apt.value.toFixed(2)}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Occurrence Dialog */}
      <Dialog open={showOccurrenceDialog} onOpenChange={setShowOccurrenceDialog}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Registrar Ocorrência</DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {selectedHuman?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-300">Tipo de Ocorrência</Label>
              <Select value={occurrenceData.type} onValueChange={(v) => setOccurrenceData({ ...occurrenceData, type: v })}>
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  {OCCURRENCE_TYPES.map(o => {
                    const Icon = o.icon;
                    return (
                      <SelectItem key={o.value} value={o.value} className="dark:text-slate-200">
                        <span className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${o.color}`} />
                          {o.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Data</Label>
              <Input
                type="date"
                value={occurrenceData.date}
                onChange={(e) => setOccurrenceData({ ...occurrenceData, date: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Descrição *</Label>
              <Textarea
                value={occurrenceData.description}
                onChange={(e) => setOccurrenceData({ ...occurrenceData, description: e.target.value })}
                placeholder="Descreva o que aconteceu..."
                rows={4}
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOccurrenceDialog(false)} className="dark:border-slate-600">
              Cancelar
            </Button>
            <Button onClick={handleAddOccurrence} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Nova Vigência de Salário */}
      <Dialog open={showNovaVigenciaDialog} onOpenChange={setShowNovaVigenciaDialog}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Registrar Alteração Salarial
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {selectedHuman?.name} - Será registrado no histórico com auditoria
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Competência de início */}
            <div>
              <Label className="dark:text-slate-300">A partir de qual competência? *</Label>
              <Input
                type="month"
                value={novaVigencia.competencia_inicio}
                onChange={(e) => setNovaVigencia({ ...novaVigencia, competencia_inicio: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600"
              />
              <p className="text-xs text-slate-500 mt-1">Mês/ano em que o novo valor passa a valer</p>
            </div>
            
            {/* Cargo */}
            <div>
              <Label className="dark:text-slate-300">Cargo *</Label>
              <Input
                placeholder="Ex: Assistente, Montador, Supervisor..."
                value={novaVigencia.cargo}
                onChange={(e) => setNovaVigencia({ ...novaVigencia, cargo: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            {/* Salário Base */}
            <div>
              <Label className="dark:text-slate-300">Novo Salário Base *</Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-500">R$</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={novaVigencia.salario_base}
                  onChange={(e) => setNovaVigencia({ ...novaVigencia, salario_base: e.target.value })}
                  className="pl-10 dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              {novaVigencia.salario_base && (
                <p className="text-xs text-green-600 mt-1">
                  Valor/hora: R$ {(parseFloat(novaVigencia.salario_base) / 220).toFixed(2)}
                </p>
              )}
            </div>
            
            {/* Motivo */}
            <div>
              <Label className="dark:text-slate-300">Motivo da alteração *</Label>
              <Select 
                value={novaVigencia.motivo} 
                onValueChange={(v) => setNovaVigencia({ ...novaVigencia, motivo: v })}
              >
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="contratacao">✅ Contratação (primeiro registro)</SelectItem>
                  <SelectItem value="promocao">🎉 Promoção</SelectItem>
                  <SelectItem value="aditivo">📝 Aditivo Contratual</SelectItem>
                  <SelectItem value="reenquadramento">🔄 Reenquadramento</SelectItem>
                  <SelectItem value="correcao_erro">⚠️ Correção de Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Justificativa (obrigatória para correção de erro) */}
            <div>
              <Label className="dark:text-slate-300">
                Justificativa {novaVigencia.motivo === 'correcao_erro' ? '*' : '(opcional)'}
              </Label>
              <Textarea
                placeholder="Descreva o motivo da alteração..."
                value={novaVigencia.justificativa}
                onChange={(e) => setNovaVigencia({ ...novaVigencia, justificativa: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600"
                rows={2}
              />
            </div>
            
            {/* Alerta sobre redução salarial */}
            {selectedHuman?.salario_base && novaVigencia.salario_base && 
             parseFloat(novaVigencia.salario_base) < selectedHuman.salario_base && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-700 dark:text-red-400">
                      Redução Salarial Detectada
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                      Art. 468 CLT veda reducao salarial. So permitido para Correcao de Erro com justificativa.
                    </p>
                    {novaVigencia.motivo === 'correcao_erro' && (
                      <div className="mt-2 flex items-center gap-2">
                        <Checkbox
                          id="aceita_reducao"
                          checked={novaVigencia.aceita_reducao}
                          onCheckedChange={(v) => setNovaVigencia({ ...novaVigencia, aceita_reducao: v })}
                        />
                        <label htmlFor="aceita_reducao" className="text-xs text-red-600 dark:text-red-300">
                          Confirmo que é correção de erro cadastral
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovaVigenciaDialog(false)} className="dark:border-slate-600">
              Cancelar
            </Button>
            <Button 
              onClick={handleAddVigencia} 
              disabled={savingVigencia || !novaVigencia.cargo.trim() || !novaVigencia.salario_base}
            >
              {savingVigencia && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Registrar Alteração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HumanasPage;
